import React, {
  useCallback,
  useContext,
  useMemo
} from 'react'
import { useHistory, Link as RouterLink } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Image, Typography } from '@telus/koodo-ui-kit'
import Icon from '@apps/components/Icon'
import PlanMfeActivation from '@apps/components/PlanMfe'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { useDispatch, useSelector } from 'react-redux'
import Sites from '@sites/index'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import envConfig from '@platformConfig/index'
import { getBySlug, getById, getByTitle } from '@apps/utils/contentful'
import {
  mfePlanToHostPlan,
  resolveGuidFromCatalog
} from '@apps/utils/planMfeUtils'
import { PLANS_STATE, updatePlansState } from '@apps/redux/features/PlansSlice'
import { PHONE_NUMBER_STATE } from '@apps/redux/features/PhoneNumberSlice'
import { PAYMENT_STATE, updatePaymentStates } from '@apps/redux/features/PayAndReviewSlice'
import { buildAutoPromoState } from '@apps/utils/autoPromo'
import MarkdownRenderer from '@/apps/components/MarkdownRenderer'

const { planMfeBffBaseUrl } = envConfig

const PlansSection = ({
  pushClickAnalytics, backLink, onAddToCart
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pageContent, featureFlags: pageFeatureFlags } = useSelector((state) => state.common.content)
  const { enableMultiPromo } = pageFeatureFlags?.[0] || {}
  const { userType } = useSelector((state) => state.common)
  const consumerActivationPaths = new ConsumerActivationPaths()
  const dealerActivationPaths = new DealerActivationPaths()
  const activationPaths = userType === Sites.dealer ? dealerActivationPaths : consumerActivationPaths
  const {
    [PLANS_STATE.PLAN]: selectedPlan,
    localizedPlanItem
  } = useSelector((state) => state.plansPage)
  const {
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: multiPromotionCodeInfo,
    [PAYMENT_STATE.PROMO.MULTI_PROMO_CODES]: multiPromoCodes
  } = useSelector((state) => state.payAndReviewPage)
  const { [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]: selectedPhoneNumber } = useSelector(
    (state) => state.phoneNumberPage
  )

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region,
    [APPLICATION_STATE.IDENTITY]: identity
  } = useContext(ApplicationContext)

  const pagePrefix = userType === Sites.dealer ? 'Dealer' : 'Consumer'
  const plansPageContent = getById(pageContent, `plansPage${pagePrefix}`)[language]
  const { shortTextFields, longTextFields, images } = plansPageContent || {}

  const textContent = useMemo(() => {
    const isFr = language === 'fr'
    return {
      planCategory: getBySlug(shortTextFields, 'plansCategory')?.value,
      pickMobilePlan: getBySlug(shortTextFields, 'pickMobilePlan')?.value,
      pickMobilePlanSubText: getBySlug(shortTextFields, 'pickMobilePlanSubText')?.value,
      selectPlanLabel: getBySlug(shortTextFields, 'selectPlan')?.value,
      selectedPlanLabel: getBySlug(shortTextFields, 'selectedPlan')?.value,
      viewDetailsLabel: getBySlug(shortTextFields, 'viewDetails')?.value
        || (isFr ? 'Voir les détails' : 'View details'),
      autoPromoText: getBySlug(shortTextFields, 'autoPromoText')?.value,
      planDurationLabels: {
        30: getBySlug(shortTextFields, 'planDuration30days')?.value,
        90: getBySlug(shortTextFields, 'planDuration90days')?.value,
        365: getBySlug(shortTextFields, 'planDuration365days')?.value
          || (isFr ? '365 jours' : '365 Days')
      },
      // Phone-with-bubbles illustration for the plans hero banner. Content
      // team uploads under one of these titles (in order of preference).
      // NOTE: use getByTitle (works on the `images` array directly) rather
      // than getImgByTitle which expects the parent page object.
      plansHeroImage: getByTitle(images, 'plansPhoneWithBubbles')
        || getByTitle(images, 'plansHeroImage')
        || getByTitle(images, 'plansHero')
        || getByTitle(images, 'phoneWithBubbles')
    }
  }, [shortTextFields, longTextFields, images, language])

  const {
    planCategory,
    pickMobilePlan,
    pickMobilePlanSubText,
    plansHeroImage,
    selectPlanLabel,
    selectedPlanLabel,
    viewDetailsLabel,
    planDurationLabels,
    autoPromoText
  } = textContent

  const normalizedPickMobilePlanSubText = useMemo(() => {
    if (typeof pickMobilePlanSubText !== 'string') return pickMobilePlanSubText
    // Contentful editors sometimes add spaces inside bold delimiters (`** text **`),
    // which markdown treats as plain text. Normalize to `**text**` for rendering.
    return pickMobilePlanSubText.replace(/\*\*\s+([\s\S]*?)\s+\*\*/g, '**$1**')
  }, [pickMobilePlanSubText])

  // MFE callbacks ─────────────────────────────────────────────────────────────

  const handleMfeSelect = useCallback((planId, mfePlan) => {
    const hostPlan = mfePlanToHostPlan(mfePlan, language, planId)
    const catalogGuid = resolveGuidFromCatalog(localizedPlanItem, language, planId)
    const resolvedGuid = catalogGuid || hostPlan?.pricePlanGuid || hostPlan?.guid
    const normalizedHostPlan = {
      ...hostPlan,
      guid: resolvedGuid,
      pricePlanGuid: resolvedGuid
    }
    const isReselect = String(selectedPlan?.id) === String(planId)
    const label = isReselect ? selectedPlanLabel : selectPlanLabel
    // Attach the plan's catalog promo to the multi-promo arrays, as
    // PlansGrid.choosePlan did before the MFE replaced the plan cards. Without
    // it the promo never reaches the order summary or the promo validation
    // payload (MASC-17223).
    const autoPromoState = buildAutoPromoState({
      promo: normalizedHostPlan.promo,
      multiPromotionCodeInfo,
      multiPromoCodes,
      autoPromoText,
      enableMultiPromo,
      resetRaf: true
    })
    if (autoPromoState) dispatch(updatePaymentStates(autoPromoState))

    dispatch(
      updatePlansState({
        [PLANS_STATE.PLAN]: normalizedHostPlan,
        [PLANS_STATE.PROMO]: normalizedHostPlan.promo
      })
    )
    if (pushClickAnalytics && label) {
      pushClickAnalytics(`${label} - ${normalizedHostPlan.name} $${normalizedHostPlan.value}`)
    }
    if (onAddToCart) {
      onAddToCart(normalizedHostPlan, { isReselect })
    }
  }, [dispatch, language, localizedPlanItem, onAddToCart, pushClickAnalytics, selectedPlan?.id, selectPlanLabel, selectedPlanLabel,
    multiPromotionCodeInfo, multiPromoCodes, autoPromoText, enableMultiPromo])

  const handleMfeLearnMore = useCallback((planId, mfePlan) => {
    if (pushClickAnalytics) {
      const name = mfePlan?.name || String(planId)
      pushClickAnalytics(viewDetailsLabel, `${viewDetailsLabel} for $${mfePlan?.price} ${name}`)
    }
  }, [pushClickAnalytics, viewDetailsLabel])

  // ────────────────────────────────────────────────────────────────────────────

  const handleMfeLoadError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.error('[PlansSection] MFE load error, redirecting to splash', { error: err })
    try {
      // Get error description from Contentful long text fields
      const errorDescription = getBySlug(longTextFields, 'mfe-error-description')?.value
      const errorCode = err?.code || err?.errorCode || 'MFE_LOAD_ERROR'
      // Prefer the description the failure itself reported — it names what went
      // wrong rather than stating that something did. Only failures with nothing
      // user-facing to say (a failed remote module load) fall through to the
      // Contentful copy, and then to a built-in string if that slug is empty too.
      const defaultDescription = language === 'fr'
        ? 'Impossible de charger le service de sélection de plans'
        : 'Unable to load plan selection service'
      // Redirect to splash page with error state
      history.push(activationPaths.Splash, {
        error: {
          code: errorCode,
          description: err?.description || errorDescription || defaultDescription,
          details: err?.message
        }
      })
    } catch (error) {
      // Fallback: just redirect to splash without error details
      // eslint-disable-next-line no-console
      console.error('[PlansSection] Error processing MFE error, redirecting to splash', { error })
      history.push(activationPaths.Splash)
    }
  }, [longTextFields, language, history, activationPaths])

  return (
    <div tabIndex="0" className="w-full">
      {pickMobilePlan && (
        <section
          className="kpre-plans-hero"
          data-testid="plans-hero"
          aria-label={plansHeroImage?.altText || pickMobilePlan}
        >
          <div className="kpre-plans-hero-inner">
            <div className="kpre-plans-hero-text">
              <Typography
                tag="h1"
                variant={{ size: 'h1', bold: true }}
                className="kpre-plans-hero-heading"
                data-testid="plans-heroHeading"
              >
                {pickMobilePlan}
              </Typography>
              {normalizedPickMobilePlanSubText && (
                <div
                  className="kpre-plans-hero-subtext"
                  data-testid="plans-heroSubText"
                >
                  <MarkdownRenderer
                    markdown={normalizedPickMobilePlanSubText}
                    paragraphClassForIndex={() => 'kpre-plans-hero-subtext-paragraph'}
                    linkPrimaryClass={false}
                    strongClass="font-bold"
                  />
                </div>
              )}
            </div>
            <div className="kpre-plans-hero-media" aria-hidden={!plansHeroImage?.url}>
              {plansHeroImage?.url && (
                <Image
                  src={plansHeroImage.url}
                  alt={plansHeroImage.altText || ''}
                  className="kpre-plans-hero-image"
                  data-testid="plans-heroImage"
                />
              )}
            </div>
            <div className="kpre-plans-hero-accent" aria-hidden="true" />
          </div>
        </section>
      )
      }

      <div className="kpre-plans-content-container">
        {(planCategory || (backLink?.to && backLink?.label)) && (
          <div className="kpre-plans-category-row flex items-center justify-between gap-4 flex-wrap">
            {planCategory && (
              <Typography
                tag="h2"
                variant={{ size: 'h2', bold: true }}
                className="kpre-plans-category-heading"
                data-testid="plans-pickAPlanHeading"
              >
                {planCategory}
              </Typography>
            )}
            {backLink?.to && backLink?.label && (
              <RouterLink
                to={backLink.to}
                onClick={backLink.onClick}
                className="kpre-back-card"
                aria-label={backLink.label}
                data-testid="plans-backToActivation"
              >
                <span className="kpre-back-card-icon" aria-hidden="true">
                  <Icon icon="chevronLeft" size={16} color="#000000" />
                </span>
                <span className="kpre-back-card-title">{backLink.label}</span>
              </RouterLink>
            )}
          </div>
        )}
        <PlanMfeActivation
          language={language}
          province={region}
          brand="koodo"
          flow="activation"
          bffBaseUrl={planMfeBffBaseUrl}
          channel={userType === Sites.dealer ? 'dealer' : 'consumer'}
          channelOrgId={
            userType === Sites.dealer ? identity?.channelOrgCode : undefined
          }
          phoneNumber={selectedPhoneNumber || undefined}
          selectedPlanId={selectedPlan?.id ? String(selectedPlan.id) : undefined}
          backLink={backLink}
          onSelect={handleMfeSelect}
          onLearnMore={handleMfeLearnMore}
          onDurationChange={(days) => {
            const label = planDurationLabels?.[days]
            if (pushClickAnalytics && label) pushClickAnalytics(label, label)
          }}
          onLoadError={handleMfeLoadError}
        />
      </div>
    </div >
  )
}

PlansSection.propTypes = {
  pushClickAnalytics: PropTypes.func,
  backLink: PropTypes.shape({
    to: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func
  }),
  onAddToCart: PropTypes.func
}

PlansSection.defaultProps = {
  pushClickAnalytics: undefined,
  backLink: undefined,
  onAddToCart: undefined
}

export default PlansSection
